import { useEffect, useState } from 'react';
import ActionPrompt from './ActionPrompt';
import Amount, { CostLabel } from './Amount';
import { PickingContext } from '../context/PickingContext';
import { AskedChoice, HandWays } from './PoliticalCards';
const { Phase } = require('@tta/engine/Moves/MoveTypes');
const { ShownName } = require('@tta/engine/Utilities/ShownNames');

// What each stat changed by a move is shown as: the kind of amount (its icon) and, for what comes each turn, "/turn"
const StatKinds = {
    culture: ['culture'], cultureProduction: ['culture', '/turn'],
    science: ['science'], scienceProduction: ['science', '/turn'],
    strength: ['strength'],
    food: ['food'], foodProduction: ['food', '/turn'],
    resources: ['resources'], resourceProduction: ['resources', '/turn'],
    happyFaces: ['happy faces'],
    civilActions: ['CA', '/turn'], militaryActions: ['MA', '/turn'],
    population: ['population'],
    yellowTokens: ['yellow tokens'], blueTokens: ['blue tokens'],
};

function Change({change})
{
    const [kind, suffix] = StatKinds[change.stat] ?? [];
    return (
        <span className={`tta-cursor-change${change.change < 0 ? ' loss' : ''}`}>
            {kind ? <Amount value={change.change} kind={kind} signed/> : `${change.change > 0 ? '+' : ''}${change.change} ${change.label}`}
            {suffix && <span className="tta-cursor-suffix">{suffix}</span>}
        </span>
    );
}

// One way to do it: the actions it spends, then its cost → what changes; the cost alone when nothing changes at once
// (developing a technology: what it brings is the card itself)
function PreviewLine({item, labelled})
{
    // (the cost's own mention of actions, e.g. a revolution's "all CA", is left to the actions counted out first)
    const paid = (item.cost ?? '').split(', ').filter(part => part && !/\b(CA|MA)$/.test(part));
    const cost = [...item.spends, ...paid].join(', ');
    return (
        <div className="tta-cursor-line">
            {labelled && <span className="tta-cursor-label">{item.label}:</span>}
            {cost ? <CostLabel cost={cost}/> : <span>free</span>}
            {item.changes.length > 0 && <>
                <span className="tta-cursor-arrow">→</span>
                {item.changes.map(change => <Change key={change.stat} change={change}/>)}
            </>}
        </div>
    );
}

// The popup beside the mouse while it is over something the action bar can do (a trigger that acts at once, or a target
// of the one switched on: anything marked data-tta-target): what it would cost and do, or why it cannot be picked
function CursorPopup({GameEngine})
{
    const [at, setAt] = useState(null);
    useEffect(() => {
        const move = (event) => {
            const key = event.target.closest?.('[data-tta-target]')?.getAttribute('data-tta-target');
            setAt(key ? { key, x: event.clientX, y: event.clientY } : null);
        };
        document.addEventListener('mousemove', move);
        return () => document.removeEventListener('mousemove', move);
    }, []);
    const preview = at && GameEngine.PreviewTarget(at.key);
    if (!preview || (preview.items && preview.items.length === 0))
    {
        return null;
    }
    // (beside the mouse, kept on screen: to its left near the right edge)
    const style = { top: at.y + 16, ...(at.x > window.innerWidth - 320 ? { right: window.innerWidth - at.x + 12 } : { left: at.x + 16 }) };
    return (
        <div className="tta-cursor-popup" style={style}>
            {preview.reason && <div className="tta-cursor-reason">{preview.reason}</div>}
            {preview.note && <div>{preview.note}</div>}
            {preview.items?.map((item, index) => <PreviewLine key={index} item={item} labelled={preview.items.length > 1}/>)}
        </div>
    );
}

// The action bar (see the engine's ActionBar.js): a button for each kind of action, greyed out (with why on hover) when
// it can't be done. Switched on, its targets are picked where they are; Esc or the button again switches it off.
// TurnButtons: the turn's own buttons (End turn, Undo, the resets), above them. Political: in the political phase, what
// is there besides its own buttons (the political actions not played from a card, and passing it). Wide: across the
// top of the board (the new UI), everything in one row, with a choice it asks for under it
function ActionBar({Bar, GameEngine, TurnButtons, Political, Wide = false})
{
    useEffect(() => {
        if (!Bar?.active)
        {
            return undefined;
        }
        const key = (event) => event.key === 'Escape' && GameEngine.StartTrigger(Bar.active);
        document.addEventListener('keydown', key);
        return () => document.removeEventListener('keydown', key);
    }, [Bar?.active, GameEngine]);
    if (!Bar)
    {
        return null;
    }
    const active = Bar.triggers.find(trigger => trigger.id === Bar.active);
    return (
        <div className={`tta-action-bar${Wide ? ' wide' : ''}`}>
            {TurnButtons}
            <div className="tta-action-bar-buttons">
                {Bar.triggers.filter(trigger => !trigger.on && (trigger.phase === Phase.Political) === !!Political).map(trigger =>
                    // (the popup by the mouse explains each: why it can't be used, what one that acts at once would do,
                    // or how it is used; so a greyed-out one is marked off rather than disabled, which would get no mouse
                    // events, and none has a browser tooltip)
                    <button key={trigger.id} type="button"
                        className={`tta-action-bar-button${trigger.active ? ' active' : ''}${trigger.enabled ? '' : ' off'}`}
                        aria-disabled={!trigger.enabled}
                        data-tta-target={`trigger:${trigger.id}`}
                        onClick={() => GameEngine.StartTrigger(trigger.id)}>
                        {trigger.label}
                    </button>)}
            </div>
            {Political && <div className="tta-action-bar-political">{Political}</div>}
            {/* (what to pick; across the top (Wide) there is none: what can be picked is outlined where it is) */}
            {active?.pick && !Wide && <div className="tta-action-bar-hint">
                {Bar.cardPick
                    ? (Bar.from ? `${Bar.cardPick.name}: upgrade ${ShownName(Bar.from)} to…` : `${Bar.cardPick.name}: pick ${Bar.cardPick.pick}`)
                    : Bar.from ? `Upgrade ${ShownName(Bar.from)} to…` : `${active.label}: pick ${active.pick}`}
            </div>}
            {Bar.choice && active?.ask && <AskedChoice Ask={active.ask} Choice={Bar.choice} GameEngine={GameEngine}/>}
            {Bar.handWays && <HandWays Ways={Bar.handWays} GameEngine={GameEngine}/>}
            {Bar.choice && !active?.ask &&
                <ActionPrompt Buttons Title={Bar.choice.title} Options={Bar.choice.options}
                    OnCancel={() => GameEngine.ChooseTarget(null)}
                    OnConfirm={(option) => GameEngine.ChooseTarget(option)}/>}
            <CursorPopup GameEngine={GameEngine}/>
        </div>
    );
}

// A card or other thing that can be a target of the action bar's trigger (key: its target's key), outlined while it
// can be picked (greyed while it cannot) and picked when clicked; as it is when no trigger is on (but a card in hand,
// which is then clicked to do its action). A target's own hover (the full card) stays shut while it is picked, as the
// popup by the mouse says what picking it would do; but for a political card (a trigger that asks next), which has no
// popup: its card is what is worth seeing.
export function BarTarget({Bar, TargetKey, GameEngine, children})
{
    const target = Bar?.active ? Bar.targets[TargetKey] : null;
    const asks = !!Bar?.triggers.find(trigger => trigger.id === Bar.active)?.ask;
    if (!Bar?.active)
    {
        // With no trigger on, a card in hand does its own action when clicked (its trigger's); what that costs is on the
        // card, and why it can't be done in its hover (the full card), so there is no popup by the mouse
        const hand = Bar?.hand?.[TargetKey];
        return hand ? (
            <div className={`tta-bar-hand${hand.selectable ? ' selectable' : ''}`}
                onClickCapture={(event) => { event.stopPropagation(); event.preventDefault(); hand.selectable && GameEngine.ClickHandCard(TargetKey); }}>
                {children}
            </div>) : children;
    }
    return (
        <div data-tta-target={target ? TargetKey : undefined}
            className={`tta-bar-target${target?.selectable ? ' selectable' : ' blocked'}${target?.mark ? ` ${target.mark}` : ''}`}
            onClickCapture={(event) => { event.stopPropagation(); event.preventDefault(); target?.selectable && GameEngine.ClickTarget(TargetKey); }}>
            <PickingContext.Provider value={!!target && !asks}>{children}</PickingContext.Provider>
        </div>
    );
}

// A leader or completed wonder with an ability of its own (On: 'leader', or 'built:<wonder>'; see the engine's
// ActionBar.js): while it can be used, outlined, and clicked to use it (or, Barbarossa, to pick what with). As it is
// otherwise, and while another trigger is on. Its own hover (the full card) stays shut while it can be used, as the
// popup by the mouse says what it would do.
export function CardAbility({Bar, On, GameEngine, children})
{
    const trigger = Bar?.triggers.find(t => t.on === On);
    if (!trigger || (Bar.active && Bar.active !== trigger.id))
    {
        return children;
    }
    return (
        <div data-tta-target={`trigger:${trigger.id}`}
            className={`tta-bar-ability${trigger.enabled ? ' selectable' : ''}${trigger.active ? ' active' : ''}`}
            onClickCapture={(event) => { event.stopPropagation(); event.preventDefault(); trigger.enabled && GameEngine.StartTrigger(trigger.id); }}>
            <PickingContext.Provider value={trigger.enabled}>{children}</PickingContext.Provider>
        </div>
    );
}

export default ActionBar;
